/**
 * WorkshopGodScene — Dev tool for editing ALL component properties at runtime.
 * Accessible from TitleScene via "COMP GOD" button.
 *
 * Layout: LEFT component list | CENTER preview + edge toggles | RIGHT categorized sliders (scrollable)
 * TOP: evolution level tabs | BOTTOM: Save / Reset / Back
 */

import Phaser from 'phaser';
import { CANVAS_WIDTH, CANVAS_HEIGHT } from '@/config/GameConfig';
import { ComponentType, COMPONENT_DEFS } from '@/components/ComponentDefs';
import { createComponentVisual, getComponentTextureKeyAtLevel, drawComponentIcon } from '@/systems/ComponentIcons';
import { createTechButton, createTechSlider, fillTechRect, TECH, showConfirmDialog } from '@/ui/TechUI';
import { createSimpleTooltip, SimpleTooltipHandle } from '@/ui/UIHelpers';
import { SoundManager } from '@/systems/SoundManager';
import {
  ComponentOverride, getOverride, setOverride, resetComponentOverrides, getOriginalDefault, getEvolvedDefault,
} from '@/config/ComponentOverrides';
import { makeRenderTexture } from '@/utils/PhaserHelpers';

// ── Layout ──
const LEFT_W = 200;
const TOP_H = 52;
const BOTTOM_H = 50;
const LIST_ITEM_H = 42;
const PREVIEW_CX = LEFT_W + 160;
const PREVIEW_CY = TOP_H + (CANVAS_HEIGHT - TOP_H - BOTTOM_H) / 2;
const EDITOR_X = LEFT_W + 340;
const EDITOR_W = CANVAS_WIDTH - EDITOR_X - 10;
const EDGE_BTN_SIZE = 30;
const EDGE_DIST = 75;
const SLIDER_GAP = 28;
const CAT_GAP = 10;
const EDITOR_BASE_Y = TOP_H + 16; // base Y for the scrollable editor container

const ALL_TYPES = Object.values(ComponentType) as ComponentType[];

// ── Slider descriptors organized by category ──
interface SliderDef {
  key: keyof ComponentOverride;
  label: string;
  min: number; max: number; step: number;
  tip: string;
}
interface SliderCategory {
  name: string;
  color: number;
  sliders: SliderDef[];
}

const SLIDER_CATEGORIES: SliderCategory[] = [
  {
    name: 'ECONOMY',
    color: 0xffcc00,
    sliders: [
      { key: 'cost', label: 'Cost $', min: 0, max: 500, step: 1,
        tip: 'Prix de placement du composant.\n+ cher = budget robot plus serré.' },
      { key: 'unlockCost', label: 'Unlock $', min: 0, max: 2000, step: 10,
        tip: 'Prix pour débloquer le composant au workshop.\n+ cher = disponible plus tard.' },
    ],
  },
  {
    name: 'CORE STATS',
    color: 0x44aaff,
    sliders: [
      { key: 'hp', label: 'HP', min: 1, max: 500, step: 1,
        tip: 'Points de vie ajoutés au robot.\n+ HP = robot plus résistant.' },
      { key: 'mass', label: 'Mass', min: 0, max: 100, step: 1,
        tip: 'Poids du composant. Affecte vitesse et inertie.\n+ masse = robot plus lent mais plus stable.' },
      { key: 'energy', label: 'Energy', min: -20, max: 50, step: 1,
        tip: 'Énergie ajoutée (ou consommée si négatif).\n+ énergie = plus d\'autonomie.\nNégatif = ce composant draine de l\'énergie.' },
    ],
  },
  {
    name: 'MOVEMENT',
    color: 0x44cc44,
    sliders: [
      { key: 'force', label: 'Force', min: 0, max: 200, step: 5,
        tip: 'Force de propulsion (roues, ailes, turbines).\n+ force = accélération et vitesse max plus élevées.' },
    ],
  },
  {
    name: 'SENSOR',
    color: 0x44ff88,
    sliders: [
      { key: 'sensorRange', label: 'Range (px)', min: 0, max: 400, step: 10,
        tip: 'Portée de détection du capteur en pixels.\n+ portée = détecte les ennemis plus loin\net révèle plus de fog of war.' },
    ],
  },
  {
    name: 'WEAPON',
    color: 0xff4444,
    sliders: [
      { key: 'damage', label: 'Damage', min: 0, max: 200, step: 1,
        tip: 'Dégâts par projectile/tir.\n+ dégâts = élimine les ennemis plus vite.' },
      { key: 'fireRate', label: 'Fire Rate', min: 0.1, max: 20, step: 0.1,
        tip: 'Cadence de tir (tirs/seconde).\n+ cadence = plus de DPS mais consomme\nplus d\'énergie.' },
      { key: 'weaponRange', label: 'Range (px)', min: 0, max: 500, step: 10,
        tip: 'Portée maximale de l\'arme en pixels.\n+ portée = engage les ennemis de plus loin.' },
      { key: 'projectileSpeed', label: 'Proj Speed', min: 0, max: 1000, step: 10,
        tip: 'Vitesse du projectile (px/s).\n+ rapide = touche plus facilement\nles cibles mobiles.' },
      { key: 'explosionDamage', label: 'Expl Dmg', min: 0, max: 200, step: 1,
        tip: 'Dégâts de l\'explosion (splash).\n+ dégâts splash = efficace contre\nles groupes d\'ennemis.' },
      { key: 'explosionRadius', label: 'Expl Radius', min: 0, max: 200, step: 5,
        tip: 'Rayon de l\'explosion en pixels.\n+ rayon = zone d\'effet plus large.' },
    ],
  },
  {
    name: 'SUPPORT',
    color: 0xaa44ff,
    sliders: [
      { key: 'repairRate', label: 'Repair/s', min: 0, max: 50, step: 1,
        tip: 'HP réparés par seconde (soi-même + alliés).\n+ réparation = sustain en combat plus élevé.' },
      { key: 'rechargeRate', label: 'Recharge/s', min: 0, max: 50, step: 1,
        tip: 'Énergie rechargée par seconde (soi-même + alliés).\n+ recharge = les alliés tiennent plus longtemps.' },
      { key: 'supportRange', label: 'Range (px)', min: 0, max: 400, step: 10,
        tip: 'Portée de l\'aura de support en pixels.\n+ portée = répare/recharge des alliés\nplus éloignés.' },
      { key: 'cooldown', label: 'Cooldown (s)', min: 0.1, max: 15, step: 0.1,
        tip: 'Temps de recharge entre activations (secondes).\nJumper: délai entre chaque push.\nMine Layer: délai entre chaque mine posée.\nExcavator: délai entre chaque récolte.\n- cooldown = action plus fréquente.' },
    ],
  },
  {
    name: 'VISUAL',
    color: 0x8888cc,
    sliders: [
      { key: 'offsetX', label: 'Offset X', min: -120, max: 120, step: 1,
        tip: 'Décalage horizontal du sprite (pixels).\nAjuste le rendu visuel, pas la hitbox.' },
      { key: 'offsetY', label: 'Offset Y', min: -120, max: 120, step: 1,
        tip: 'Décalage vertical du sprite (pixels).\nAjuste le rendu visuel, pas la hitbox.' },
      { key: 'scaleX', label: 'Scale X', min: 0.2, max: 3, step: 0.1,
        tip: 'Échelle horizontale du sprite.\n+ grand = composant plus large visuellement.' },
      { key: 'scaleY', label: 'Scale Y', min: 0.2, max: 3, step: 0.1,
        tip: 'Échelle verticale du sprite.\n+ grand = composant plus haut visuellement.' },
    ],
  },
];

export class WorkshopGodScene extends Phaser.Scene {
  private selectedType: ComponentType = ComponentType.STRUCTURE;
  private selectedLevel = 0;
  private workingOverride: ComponentOverride = {};

  // UI references
  private listContainer!: Phaser.GameObjects.Container;
  private listItems: Phaser.GameObjects.Container[] = [];
  private previewVisual: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite | null = null;
  private previewContainer!: Phaser.GameObjects.Container;
  private tabBgs: Phaser.GameObjects.Rectangle[] = [];
  private tabTexts: Phaser.GameObjects.Text[] = [];
  private edgeBtns: { gfx: Phaser.GameObjects.Graphics; idx: number; hit: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[] = [];
  private sliders: Record<string, { refresh: (v: number) => void }> = {};
  private rotBtns: Phaser.GameObjects.Container[] = [];
  private flipXBtn!: Phaser.GameObjects.Container;
  private flipYBtn!: Phaser.GameObjects.Container;
  private gridModeBtn!: Phaser.GameObjects.Container;

  // Scrollable editor
  private editorScroll!: Phaser.GameObjects.Container;
  private editorScrollY = 0;
  private editorMaxScroll = 0;
  private editorMaskShape!: Phaser.GameObjects.Graphics;

  // Tooltip overlay (above everything)
  private tooltipHandle!: SimpleTooltipHandle;

  // Scroll state
  private listScrollY = 0;
  private wheelHandler: ((p: Phaser.Input.Pointer, gx: number[], gy: number, dz: number) => void) | null = null;

  constructor() { super({ key: 'WorkshopGodScene' }); }

  create(): void {
    // Reset all UI references (scene re-entry: old game objects are destroyed)
    this.listItems = [];
    this.tabBgs = [];
    this.tabTexts = [];
    this.edgeBtns = [];
    this.sliders = {};
    this.rotBtns = [];
    this.editorScrollY = 0;
    this.listScrollY = 0;
    this.previewVisual = null;
    this.wheelHandler = null;

    this.tooltipHandle = createSimpleTooltip(this);

    this.cameras.main.setBackgroundColor('#0a0a18');
    this.buildBackground();
    this.buildEvolutionTabs();
    this.buildComponentList();
    this.buildPreviewPanel();
    this.buildEditorPanel();
    this.buildActionBar();
    this.selectComponent(ComponentType.STRUCTURE, true); // skip commitWorking on first call (workingOverride is empty)

    this.input.keyboard?.on('keydown-ESC', () => {
      SoundManager.play('click');
      this.commitWorking();
      this.scene.start('TitleScene');
    });
  }

  // ── Background ──

  private buildBackground(): void {
    const gfx = this.add.graphics();
    fillTechRect(gfx, 0, 0, CANVAS_WIDTH, TOP_H, 0x111122, 0.9);
    this.add.text(CANVAS_WIDTH / 2, TOP_H / 2, 'WORKSHOP GOD', {
      fontSize: '18px', color: '#ff88ff', fontFamily: 'monospace', fontStyle: 'bold',
    }).setOrigin(0.5);
    fillTechRect(gfx, 0, TOP_H, LEFT_W, CANVAS_HEIGHT - TOP_H - BOTTOM_H, 0x0d0d1e, 0.95);
    fillTechRect(gfx, 0, CANVAS_HEIGHT - BOTTOM_H, CANVAS_WIDTH, BOTTOM_H, 0x111122, 0.9);
  }

  // ── Evolution tabs ──

  private buildEvolutionTabs(): void {
    const labels = ['BASE', 'Lv.1', 'Lv.2', 'Lv.3'];
    const startX = LEFT_W + 30;
    const tw = 62, th = 26;
    for (let i = 0; i < labels.length; i++) {
      const cx = startX + i * 70;
      const cy = TOP_H / 2;
      const bg = this.add.rectangle(cx, cy, tw, th, 0x222244, 0.7)
        .setInteractive({ useHandCursor: true }).setDepth(3);
      const txt = this.add.text(cx, cy, labels[i], {
        fontSize: '10px', color: '#666688', fontFamily: 'monospace', fontStyle: 'bold',
      }).setOrigin(0.5).setDepth(4);
      bg.on('pointerdown', () => this.selectLevel(i));
      this.tabBgs.push(bg);
      this.tabTexts.push(txt);
    }
  }

  // ── Component list ──

  private buildComponentList(): void {
    this.listContainer = this.add.container(0, TOP_H);
    const contentH = CANVAS_HEIGHT - TOP_H - BOTTOM_H;
    const maskShape = this.make.graphics({});
    maskShape.fillRect(0, TOP_H, LEFT_W, contentH);
    this.listContainer.setMask(maskShape.createGeometryMask());

    for (let i = 0; i < ALL_TYPES.length; i++) {
      const type = ALL_TYPES[i];
      const def = COMPONENT_DEFS[type];
      const y = i * LIST_ITEM_H + LIST_ITEM_H / 2;
      const item = this.add.container(0, y);
      const bg = this.add.rectangle(LEFT_W / 2, 0, LEFT_W - 4, LIST_ITEM_H - 2, 0x222244, 0.5)
        .setInteractive({ useHandCursor: true });
      item.add(bg);
      const icon = createComponentVisual(this, type, 56, def.color);
      icon.setPosition(28, 0);
      item.add(icon);
      item.add(this.add.text(58, 0, def.name, {
        fontSize: '9px', color: '#ccccdd', fontFamily: 'monospace',
      }).setOrigin(0, 0.5));
      bg.on('pointerdown', () => this.selectComponent(type));
      this.listContainer.add(item);
      this.listItems.push(item);
    }

    this.wheelHandler = (p, _gx, _dx, dy) => {
      // Left panel scroll
      if (p.x < LEFT_W) {
        const maxScroll = Math.max(0, ALL_TYPES.length * LIST_ITEM_H - contentH);
        this.listScrollY = Phaser.Math.Clamp(this.listScrollY + dy * 0.5, 0, maxScroll);
        this.listContainer.y = TOP_H - this.listScrollY;
      }
      // Right editor scroll
      if (p.x > EDITOR_X - 20) {
        this.editorScrollY = Phaser.Math.Clamp(this.editorScrollY + dy * 0.5, 0, this.editorMaxScroll);
        this.editorScroll.y = EDITOR_BASE_Y - this.editorScrollY;
      }
    };
    this.input.on('wheel', this.wheelHandler);
  }

  // ── Preview + edges ──

  private buildPreviewPanel(): void {
    this.previewContainer = this.add.container(PREVIEW_CX, PREVIEW_CY);
    const cellGfx = this.add.graphics();
    cellGfx.lineStyle(1, 0x444466, 0.5);
    cellGfx.strokeRect(-36, -36, 72, 72);
    this.previewContainer.add(cellGfx);

    const positions = [
      { x: 0, y: -EDGE_DIST, label: 'T' },
      { x: EDGE_DIST, y: 0, label: 'R' },
      { x: 0, y: EDGE_DIST, label: 'B' },
      { x: -EDGE_DIST, y: 0, label: 'L' },
    ];
    this.edgeBtns = [];
    for (let i = 0; i < 4; i++) {
      const pos = positions[i];
      const gfx = this.add.graphics();
      this.previewContainer.add(gfx);
      const hit = this.add.rectangle(pos.x, pos.y, EDGE_BTN_SIZE, EDGE_BTN_SIZE, 0x000000, 0)
        .setInteractive({ useHandCursor: true });
      this.previewContainer.add(hit);
      const label = this.add.text(PREVIEW_CX + pos.x, PREVIEW_CY + pos.y, pos.label, {
        fontSize: '9px', color: '#ffffff', fontFamily: 'monospace',
      }).setOrigin(0.5).setDepth(5);
      hit.on('pointerdown', () => this.toggleEdge(i));
      this.edgeBtns.push({ gfx, idx: i, hit, label });
    }

    // Rotation buttons below preview
    const rotY = PREVIEW_CY + EDGE_DIST + 40;
    this.add.text(PREVIEW_CX - 60, rotY, 'ROT', {
      fontSize: '9px', color: '#aaaacc', fontFamily: 'monospace',
    }).setOrigin(0, 0.5);
    const rotLabels = ['0°', '90°', '180°', '270°'];
    const rotValues = [0, Math.PI / 2, Math.PI, Math.PI * 1.5];
    this.rotBtns = [];
    for (let i = 0; i < 4; i++) {
      const btn = createTechButton(this, PREVIEW_CX - 30 + i * 42, rotY, rotLabels[i], {
        onClick: () => this.onRotationChange(rotValues[i]),
        width: 36, height: 22, fontSize: '9px', color: TECH.DIM,
      });
      this.rotBtns.push(btn);
    }

    // Flip buttons
    const flipY2 = rotY + 30;
    this.add.text(PREVIEW_CX - 60, flipY2, 'FLIP', {
      fontSize: '9px', color: '#aaaacc', fontFamily: 'monospace',
    }).setOrigin(0, 0.5);
    this.flipXBtn = createTechButton(this, PREVIEW_CX - 10, flipY2, 'FLIP X', {
      onClick: () => this.onFlipToggle('flipX'),
      width: 50, height: 22, fontSize: '9px', color: TECH.DIM,
    });
    this.flipYBtn = createTechButton(this, PREVIEW_CX + 46, flipY2, 'FLIP Y', {
      onClick: () => this.onFlipToggle('flipY'),
      width: 50, height: 22, fontSize: '9px', color: TECH.DIM,
    });

    // Grid click mode toggle (rotate vs flip)
    const gridModeY = flipY2 + 28;
    this.gridModeBtn = createTechButton(this, PREVIEW_CX + 18, gridModeY, 'GRID: ROTATE', {
      onClick: () => this.onGridModeToggle(),
      width: 110, height: 22, fontSize: '9px', color: TECH.DIM,
    });
  }

  // ── Editor panel (scrollable, categorized) ──

  private buildEditorPanel(): void {
    const panelTop = TOP_H + 8;
    const panelH = CANVAS_HEIGHT - TOP_H - BOTTOM_H - 16;

    // Background for editor area
    const bgGfx = this.add.graphics();
    fillTechRect(bgGfx, EDITOR_X - 10, panelTop, EDITOR_W + 20, panelH, 0x0d0d1e, 0.85);

    // Scrollable container
    this.editorScroll = this.add.container(EDITOR_X, EDITOR_BASE_Y);
    this.editorMaskShape = this.make.graphics({});
    this.editorMaskShape.fillRect(EDITOR_X - 10, panelTop, EDITOR_W + 20, panelH);
    this.editorScroll.setMask(this.editorMaskShape.createGeometryMask());

    // Sliders will be created in rebuildEditorSliders (depends on selected component category)
    this.rebuildEditorSliders();
  }

  /** Rebuild editor sliders based on the selected component's category */
  private rebuildEditorSliders(): void {
    // Clear existing children
    this.editorScroll.removeAll(true);
    this.sliders = {};
    this.tooltipHandle.hide();

    const def = COMPONENT_DEFS[this.selectedType];
    let y = 4;

    for (const cat of SLIDER_CATEGORIES) {
      // Category header
      const header = this.add.text(0, y, cat.name, {
        fontSize: '10px', color: `#${cat.color.toString(16).padStart(6, '0')}`,
        fontFamily: 'monospace', fontStyle: 'bold',
      });
      this.editorScroll.add(header);
      // Underline
      const line = this.add.graphics();
      line.lineStyle(1, cat.color, 0.3);
      line.lineBetween(0, y + 12, EDITOR_W - 20, y + 12);
      this.editorScroll.add(line);
      y += 20;

      for (const sd of cat.sliders) {
        const slider = createTechSlider(
          this, 0, y, sd.label, sd.min, sd.max, sd.step,
          (this.workingOverride[sd.key] as number) ?? getEvolvedDefault(this.selectedType, this.selectedLevel, sd.key as string) ?? 0,
          (v) => this.onSliderChange(sd.key as string, v),
          EDITOR_W - 120,
        );
        this.editorScroll.add(slider);
        this.sliders[sd.key] = slider;

        // "?" help icon next to the label
        const helpBtn = this.add.text(75, y, '?', {
          fontSize: '10px', color: '#000000', fontFamily: 'monospace', fontStyle: 'bold',
          backgroundColor: '#6688aa', padding: { x: 3, y: 1 },
        }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
        helpBtn.on('pointerover', (p: Phaser.Input.Pointer) => this.tooltipHandle.show(p.worldX, p.worldY, sd.tip));
        helpBtn.on('pointerout', () => this.tooltipHandle.hide());
        this.editorScroll.add(helpBtn);

        y += SLIDER_GAP;
      }
      y += CAT_GAP;
    }

    const panelH = CANVAS_HEIGHT - TOP_H - BOTTOM_H - 16;
    this.editorMaxScroll = Math.max(0, y - panelH + 20);
    this.editorScrollY = 0;
    this.editorScroll.y = EDITOR_BASE_Y;
  }

  // ── Tooltip ──

  // Tooltip handled by shared UIHelpers.createSimpleTooltip (this.tooltipHandle)

  // ── Action bar ──

  private buildActionBar(): void {
    const y = CANVAS_HEIGHT - BOTTOM_H / 2;
    createTechButton(this, CANVAS_WIDTH / 2 - 140, y, 'SAVE', {
      onClick: () => showConfirmDialog(this, 'Save component overrides?', () => {
        this.saveAll();
      }, { confirmColor: TECH.SUCCESS }),
      width: 80, height: 30, fontSize: '13px', color: TECH.SUCCESS,
    });
    createTechButton(this, CANVAS_WIDTH / 2, y, 'RESET', {
      onClick: () => showConfirmDialog(this, 'Reset all component overrides?', () => {
        this.resetAll();
      }),
      width: 80, height: 30, fontSize: '13px', color: TECH.DANGER,
    });
    createTechButton(this, CANVAS_WIDTH / 2 + 140, y, 'BACK', {
      onClick: () => { SoundManager.play('click'); this.commitWorking(); this.scene.start('TitleScene'); },
      width: 80, height: 30, fontSize: '13px', color: TECH.COLOR,
    });
  }

  // ── Selection ──

  private selectComponent(type: ComponentType, skipCommit = false): void {
    if (!skipCommit) this.commitWorking();
    this.selectedType = type;
    this.selectedLevel = 0;
    this.workingOverride = getOverride(type, 0);

    for (let i = 0; i < ALL_TYPES.length; i++) {
      const bg = this.listItems[i].getAt(0) as Phaser.GameObjects.Rectangle;
      bg.setFillStyle(ALL_TYPES[i] === type ? 0x3344aa : 0x222244, ALL_TYPES[i] === type ? 0.8 : 0.5);
    }

    this.refreshTabs();
    this.refreshPreview();
    this.refreshEdgeButtons();
    this.refreshFlipButtons();
    this.refreshGridModeButton();
    this.rebuildEditorSliders(); // rebuild because visible categories change per component
  }

  private selectLevel(level: number): void {
    if (level > COMPONENT_DEFS[this.selectedType].maxEvolutionLevel) return;
    this.commitWorking();
    this.selectedLevel = level;
    this.workingOverride = getOverride(this.selectedType, level);
    this.refreshTabs();
    this.refreshPreview();
    this.refreshEdgeButtons();
    this.refreshEditors();
  }

  // ── Refresh ──

  private refreshTabs(): void {
    const maxLv = COMPONENT_DEFS[this.selectedType].maxEvolutionLevel;
    for (let i = 0; i < this.tabBgs.length; i++) {
      const isActive = i === this.selectedLevel;
      const isAvail = i <= maxLv;
      this.tabBgs[i].setFillStyle(isActive ? TECH.ACCENT : (isAvail ? 0x222244 : 0x111122), isActive ? 0.6 : 0.5);
      this.tabBgs[i].setAlpha(isAvail ? 1 : 0.3);
      this.tabTexts[i].setColor(isActive ? '#ffffff' : (isAvail ? '#8888aa' : '#444455'));
    }
  }

  private refreshPreview(): void {
    if (this.previewVisual) { this.previewVisual.destroy(); this.previewVisual = null; }
    const def = COMPONENT_DEFS[this.selectedType];
    const ov = this.workingOverride;

    // Build sprite manually to avoid createComponentVisual's override application
    // (preview applies workingOverride directly, not stored overrides)
    const texKey = getComponentTextureKeyAtLevel(this.selectedType, this.selectedLevel);
    let visual: Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite;
    if (texKey && this.textures.exists(texKey)) {
      const sprite = this.add.sprite(0, 0, texKey);
      const scale = 90 / Math.max(sprite.width, sprite.height);
      sprite.setScale(scale);
      visual = sprite;
    } else {
      // Graphics fallback — use drawComponentIcon via a fresh sprite
      const genKey = `comp_gen_${this.selectedType}_90`;
      if (!this.textures.exists(genKey)) {
        const pad = 4;
        const texSize = 90 + pad * 2;
        const rt = makeRenderTexture(this, texSize, texSize);
        const gfx = this.make.graphics({});
        drawComponentIcon(gfx, this.selectedType, 90, def.color);
        rt.draw(gfx, texSize / 2, texSize / 2);
        rt.saveTexture(genKey);
        rt.destroy();
        gfx.destroy();
      }
      visual = this.add.sprite(0, 0, genKey);
    }

    // Apply working override transforms — mirrors Workshop grid & Robot rendering
    const rot = ov.defaultRotation ?? getOriginalDefault(this.selectedType, 'defaultRotation') ?? 0;
    const rawOx = ov.offsetX ?? getOriginalDefault(this.selectedType, 'offsetX') ?? 0;
    const rawOy = ov.offsetY ?? getOriginalDefault(this.selectedType, 'offsetY') ?? 0;
    const cos = Math.cos(rot);
    const sin = Math.sin(rot);
    visual.setPosition(rawOx * cos - rawOy * sin, rawOx * sin + rawOy * cos);
    const sx = visual.scaleX * (ov.scaleX ?? getOriginalDefault(this.selectedType, 'scaleX') ?? 1) * (ov.flipX ? -1 : 1);
    const sy = visual.scaleY * (ov.scaleY ?? getOriginalDefault(this.selectedType, 'scaleY') ?? 1) * (ov.flipY ? -1 : 1);
    visual.setScale(sx, sy);
    visual.setRotation(rot);
    this.previewContainer.add(visual);
    this.previewContainer.moveTo(visual, 1);
    this.previewVisual = visual;
  }

  private refreshEdgeButtons(): void {
    const def = COMPONENT_DEFS[this.selectedType];
    const edges = this.workingOverride.allowedEdges ?? [...def.allowedEdges] as [boolean, boolean, boolean, boolean];
    const forbidden = this.workingOverride.forbiddenEdges ?? [...def.forbiddenEdges] as [boolean, boolean, boolean, boolean];
    const positions = [
      { x: 0, y: -EDGE_DIST }, { x: EDGE_DIST, y: 0 },
      { x: 0, y: EDGE_DIST }, { x: -EDGE_DIST, y: 0 },
    ];
    for (const eb of this.edgeBtns) {
      const isOpen = edges[eb.idx];
      const isForbidden = forbidden[eb.idx];
      const pos = positions[eb.idx];
      const s = EDGE_BTN_SIZE / 2;
      eb.gfx.clear();
      if (isOpen) {
        // Open: green
        eb.gfx.fillStyle(TECH.SUCCESS, 0.35);
        eb.gfx.fillRoundedRect(pos.x - s, pos.y - s, EDGE_BTN_SIZE, EDGE_BTN_SIZE, 4);
        eb.gfx.lineStyle(1.5, TECH.SUCCESS, 0.8);
        eb.gfx.strokeRoundedRect(pos.x - s, pos.y - s, EDGE_BTN_SIZE, EDGE_BTN_SIZE, 4);
      } else if (isForbidden) {
        // Forbidden: red filled + X
        eb.gfx.fillStyle(TECH.DANGER, 0.5);
        eb.gfx.fillRoundedRect(pos.x - s, pos.y - s, EDGE_BTN_SIZE, EDGE_BTN_SIZE, 4);
        eb.gfx.lineStyle(1.5, TECH.DANGER, 0.9);
        eb.gfx.strokeRoundedRect(pos.x - s, pos.y - s, EDGE_BTN_SIZE, EDGE_BTN_SIZE, 4);
        // X mark
        const xs = s * 0.5;
        eb.gfx.lineStyle(2, 0xffffff, 0.8);
        eb.gfx.lineBetween(pos.x - xs, pos.y - xs, pos.x + xs, pos.y + xs);
        eb.gfx.lineBetween(pos.x - xs, pos.y + xs, pos.x + xs, pos.y - xs);
      } else {
        // Blocked: red outline only
        eb.gfx.lineStyle(1.5, TECH.DANGER, 0.7);
        eb.gfx.strokeRoundedRect(pos.x - s, pos.y - s, EDGE_BTN_SIZE, EDGE_BTN_SIZE, 4);
      }
    }
  }

  private refreshEditors(): void {
    const ov = this.workingOverride;
    for (const cat of SLIDER_CATEGORIES) {
      for (const sd of cat.sliders) {
        this.sliders[sd.key]?.refresh((ov[sd.key] as number) ?? getEvolvedDefault(this.selectedType, this.selectedLevel, sd.key as string) ?? 0);
      }
    }
    // Rotation buttons
    const rot = ov.defaultRotation ?? (getOriginalDefault(this.selectedType, 'defaultRotation') ?? 0);
    const rotValues = [0, Math.PI / 2, Math.PI, Math.PI * 1.5];
    for (let i = 0; i < this.rotBtns.length; i++) {
      this.rotBtns[i].setAlpha(Math.abs(rotValues[i] - rot) < 0.01 ? 1 : 0.5);
    }
    this.refreshFlipButtons();
    this.refreshGridModeButton();
  }

  private refreshFlipButtons(): void {
    const fx = this.workingOverride.flipX ?? false;
    const fy = this.workingOverride.flipY ?? false;
    this.flipXBtn.setAlpha(fx ? 1 : 0.5);
    this.flipYBtn.setAlpha(fy ? 1 : 0.5);
  }

  // ── Input ──

  /** Cycle edge state: open → blocked → forbidden → open */
  private toggleEdge(idx: number): void {
    SoundManager.play('click');
    const def = COMPONENT_DEFS[this.selectedType];
    if (!this.workingOverride.allowedEdges) {
      this.workingOverride.allowedEdges = [...def.allowedEdges] as [boolean, boolean, boolean, boolean];
    }
    if (!this.workingOverride.forbiddenEdges) {
      this.workingOverride.forbiddenEdges = [...def.forbiddenEdges] as [boolean, boolean, boolean, boolean];
    }
    const isOpen = this.workingOverride.allowedEdges[idx];
    const isForbidden = this.workingOverride.forbiddenEdges[idx];

    if (isOpen) {
      // open → blocked
      this.workingOverride.allowedEdges[idx] = false;
      this.workingOverride.forbiddenEdges[idx] = false;
    } else if (!isForbidden) {
      // blocked → forbidden
      this.workingOverride.allowedEdges[idx] = false;
      this.workingOverride.forbiddenEdges[idx] = true;
    } else {
      // forbidden → open
      this.workingOverride.allowedEdges[idx] = true;
      this.workingOverride.forbiddenEdges[idx] = false;
    }
    this.refreshEdgeButtons();
    this.commitWorking();
  }

  private onSliderChange(key: string, value: number): void {
    (this.workingOverride as any)[key] = value;
    // Visual sliders update preview live
    if (['offsetX', 'offsetY', 'scaleX', 'scaleY', 'defaultRotation'].includes(key)) {
      this.refreshPreview();
    }
    this.commitWorking(); // auto-save on every change
  }

  private onRotationChange(radians: number): void {
    SoundManager.play('click');
    this.workingOverride.defaultRotation = radians;
    this.refreshPreview();
    this.refreshEditors();
    this.commitWorking(); // auto-save
  }

  private onFlipToggle(key: 'flipX' | 'flipY'): void {
    SoundManager.play('click');
    this.workingOverride[key] = !this.workingOverride[key];

    // Swap edge data to match the flipped sprite:
    // flipX → L↔R (indices 3↔1), flipY → T↔B (indices 0↔2)
    const def = COMPONENT_DEFS[this.selectedType];
    if (!this.workingOverride.allowedEdges) {
      this.workingOverride.allowedEdges = [...def.allowedEdges] as [boolean, boolean, boolean, boolean];
    }
    if (!this.workingOverride.forbiddenEdges) {
      this.workingOverride.forbiddenEdges = [...def.forbiddenEdges] as [boolean, boolean, boolean, boolean];
    }
    const [a, b] = key === 'flipX' ? [1, 3] : [0, 2];
    // Swap allowed
    const tmpA = this.workingOverride.allowedEdges[a];
    this.workingOverride.allowedEdges[a] = this.workingOverride.allowedEdges[b];
    this.workingOverride.allowedEdges[b] = tmpA;
    // Swap forbidden
    const tmpF = this.workingOverride.forbiddenEdges[a];
    this.workingOverride.forbiddenEdges[a] = this.workingOverride.forbiddenEdges[b];
    this.workingOverride.forbiddenEdges[b] = tmpF;

    this.refreshPreview();
    this.refreshFlipButtons();
    this.refreshEdgeButtons();
    this.commitWorking(); // auto-save
  }

  private onGridModeToggle(): void {
    SoundManager.play('click');
    const current = this.workingOverride.gridClickMode ?? 'rotate';
    this.workingOverride.gridClickMode = current === 'rotate' ? 'flip' : 'rotate';
    this.refreshGridModeButton();
    this.commitWorking(); // auto-save
  }

  private refreshGridModeButton(): void {
    const mode = this.workingOverride.gridClickMode ?? 'rotate';
    const label = mode === 'flip' ? 'GRID: FLIP' : 'GRID: ROTATE';
    const txt = this.gridModeBtn.getAt(1) as Phaser.GameObjects.Text;
    if (txt) txt.setText(label);
    this.gridModeBtn.setAlpha(mode === 'flip' ? 1 : 0.5);
  }

  // ── Persistence ──

  private commitWorking(): void {
    setOverride(this.selectedType, this.selectedLevel, this.workingOverride);
  }

  private saveAll(): void {
    this.commitWorking();
    SoundManager.play('clickConfirm');
    this.cameras.main.flash(200, 68, 255, 68);
  }

  private resetAll(): void {
    SoundManager.play('click');
    resetComponentOverrides();
    this.workingOverride = {};
    this.refreshPreview();
    this.refreshEdgeButtons();
    this.rebuildEditorSliders();
  }

  shutdown(): void {
    this.tooltipHandle.hide();
    if (this.wheelHandler) {
      this.input.off('wheel', this.wheelHandler);
      this.wheelHandler = null;
    }
  }
}
